import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  LightMode,
  Stack,
  Text,
  Tooltip,
  useColorModeValue as mode,
  useDisclosure,
} from '@chakra-ui/react';
import { documentAction } from 'actions';
import AmatriumTableDocument from 'components/AmatriumTableDocument';
import ConfirmModal from 'components/Modal/ConfirmModal';
import DeleteModal from 'components/Modal/DeleteModal';
import Paginate from 'components/Paginate';
import StateText from 'components/StateText';
import configs from 'configs';
import FilePreviewDialog, { PreviewFileState } from 'containers/Summary/FilePreviewDialog';
import { DEFAULT_BEGIN_PAGE, DEFAULT_SIZE_PAGE, HTTP_CODE_RESPONSE } from 'helpers/constant';
import { ROLES } from 'helpers/enum';
import { addTokenURL, getFileExtension } from 'helpers/file';
import { getUserRolesStorage } from 'helpers/lib';
import { getStorage, STORAGE_KEY } from 'helpers/storage';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoEye } from 'react-icons/io5';
import { RiDeleteBin6Fill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { FileDocumentState } from 'reducer/document';
import { UploadService } from 'services/upload/upload.service';
import { FileDocument } from 'types/reducers/document';
import { getStorageReadBlobToken } from 'utils/blob';
import './index.css';

const Document = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { data, total_item, loading } = useSelector((state) => state.document);
  const selectedFiles = useSelector(
    (state) => state.document.data?.filter((file) => file.selected)
  );

  const [currentPage, setCurrentPage] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [isShowingPreviewFile, setIsShowingPreviewFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewFileState>({});
  const [deleteFile, setDeleteFile] = useState<FileDocument>();

  const {
    isOpen: isOpenDeleteDialog,
    onOpen: onOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
  } = useDisclosure();

  const {
    isOpen: isOpenDeleteItemDialog,
    onOpen: onOpenDeleteItemDialog,
    onClose: onCloseDeleteItemDialog,
  } = useDisclosure();

  const isSelectedFile = selectedFiles?.length > 0;
  const userRoles = getUserRolesStorage();
  const isAdmin = userRoles?.includes(ROLES.ADMIN) || userRoles?.includes(ROLES.SUPER_ADMIN);

  useEffect(() => {
    dispatch(
      documentAction.fetchFiles({
        page: DEFAULT_BEGIN_PAGE,
        fileName: '',
        sortField: getStorage(STORAGE_KEY.SORT_FIELD) || 'created_at',
        isAscending: false,
      })
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteFiles = (files: FileDocumentState[]) => {
    dispatch(documentAction.deleteFiles(files.map((file) => file.id)));
    // Reset to first page after delete
    setCurrentPage(0);
    setSearchValue('');
  };

  const handleChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchValue(event.target.value);

  const onRowSelect = (row: FileDocumentState, _rowId: string) => {
    dispatch(
      documentAction.updateSelectFile({
        id: row.id,
      })
    );
  };

  const getDocumentFilterByName = () => {
    setSearchValue(searchValue?.trim());
    dispatch(
      documentAction.fetchFiles({
        page: DEFAULT_BEGIN_PAGE,
        fileName: searchValue?.trim(),
        sortField: 'created_at',
        isAscending: false,
      })
    );
    setCurrentPage(0);
  };

  const handleSelectAllFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(documentAction.selectAllFile(e.target.checked));
    },
    [dispatch]
  );

  const handleView = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: FileDocument) => {
      e.stopPropagation();
      onOpenFile(item);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: FileDocument) => {
      e.stopPropagation();
      setDeleteFile(item);
      onOpenDeleteItemDialog();
    },
    [onOpenDeleteItemDialog]
  );

  const onDeleteItem = () => {
    if (deleteFile) {
      deleteFiles([deleteFile]);
    }
  };

  const tableColumns = useMemo(() => {
    return [
      ...(isAdmin
        ? [
            {
              Header: 'Select',
              accessor: 'selected',
              key: 'selected',
              Filter: '',
              filter: '',
              disableSortBy: true,
              type: 'checkbox',
              width: '60px',
              titleClickHandler: handleSelectAllFile,
            },
          ]
        : []),
      {
        Header: t('document.file'),
        accessor: 'name',
        key: 'name',
        Filter: '',
        filter: '',
        width: 'auto',
        render: (info: any) => {
          return (
            <Tooltip p={2} whiteSpace={'pre-wrap'} label={info.name}>
              <Text overflow={'hidden'} whiteSpace={'pre'} textOverflow={'ellipsis'}>
                {info.name}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        Header: t('document.source'),
        accessor: 'source',
        key: 'source',
        Filter: '',
        filter: '',
        disableSortBy: true,
        render: (info: any) => {
          return <>{info?.source ? <StateText statusKey={info.source} /> : null}</>;
        },
      },
      {
        Header: t('document.state'),
        accessor: 'state',
        key: 'state',
        Filter: '',
        filter: '',
        render: (info: any) => {
          return (
            <Text overflow={'hidden'} textOverflow={'ellipsis'}>
              {info?.state ? <StateText statusKey={info.state} /> : null}
            </Text>
          );
        },
      },
      {
        Header: t('document.uploadedBy'),
        accessor: 'uploadedBy',
        key: 'uploadedBy',
        Filter: '',
        filter: '',
        width: 200,
        disableSortBy: true,
        render: (info: any) => {
          return (
            <Tooltip p={2} label={info.uploadedBy}>
              <Text overflow={'hidden'} textOverflow={'ellipsis'}>
                {info?.uploadedBy ? <>{info.uploadedBy}</> : null}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        Header: t('document.uploadedAt'),
        accessor: 'created_date',
        key: 'created_date',
        Filter: '',
        filter: '',
        width: 200,
      },
      {
        Header: t('document.size'),
        accessor: 'size',
        key: 'size',
        Filter: '',
        filter: '',
        render: (info: any) => {
          return info.size ? <>{info.size.toFixed(2)}</> : null;
        },
      },
      {
        Header: t('document.actions'),
        accessor: 'action',
        key: 'action',
        Filter: '',
        filter: '',
        disableSortBy: true,
        actionItems: [
          ...(isAdmin
            ? [
                {
                  key: 'view',
                  'aria-label': 'View file',
                  label: t('common.view'),
                  icon: <IoEye />,
                  onClick: handleView,
                  color: 'blue.300',
                },
                {
                  key: t('common.delete'),
                  'aria-label': 'Delete file',
                  label: t('common.delete'),
                  icon: <RiDeleteBin6Fill />,
                  onClick: handleDelete,
                  color: 'red.400',
                },
              ]
            : [
                {
                  key: 'view',
                  'aria-label': 'View file',
                  label: t('common.view'),
                  icon: <IoEye />,
                  onClick: handleView,
                  color: 'blue.300',
                  textAlign: 'center',
                },
              ]),
        ],
      },
    ];
  }, [t, isAdmin, handleSelectAllFile, handleView, handleDelete]);

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = configs.setting.TIMEOUT_REFRESH_SAS_TOKEN_IN_MS;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const onOpenFile = (file: FileDocument) => {
    readFile(file);
  };

  const readFile = (file: FileDocument) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(file);
  };

  const fetchData = (file: FileDocument) => {
    const fileUrl = addTokenURL(file?.url, getStorageReadBlobToken());
    setPreviewFile({
      url: fileUrl,
      name: file?.name,
      type: getFileExtension(file?.url),
    });

    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(fileUrl);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(file);
        } else {
          setIsShowingPreviewFile(true);
        }
      } catch (error) {
        console.log(error);
      }
    })();
  };

  const onClose = () => {
    setIsShowingPreviewFile(false);
  };

  return (
    <>
      <FilePreviewDialog isOpen={isShowingPreviewFile} file={previewFile} onClose={onClose} />
      <Stack>
        <DeleteModal
          isOpen={isOpenDeleteDialog}
          onClose={onCloseDeleteDialog}
          recordDelete={selectedFiles}
          action={deleteFiles}
          dataHeader={t('document.deleteFile')}
          dataBody={t('document.deleteFileInstruction')}
          btnAction={t('common.delete')}
          color={'red'}
        />
        <ConfirmModal
          isOpen={isOpenDeleteItemDialog}
          onClose={onCloseDeleteItemDialog}
          action={onDeleteItem}
          dataHeader={`${t('document.deleteFile')} ${deleteFile?.name}`}
          dataBody={t('document.deleteFileInstruction')}
          btnAction={t('common.delete')}
          color={'red'}
        />
        <Stack>
          <Flex
            mb={{
              base: '0.8rem',
              md: '1rem',
              xl: '1.25rem',
              '2xl': '1.25rem',
            }}
          >
            <Heading as='h3' mr='1rem' fontWeight={'bold'} size='md'>
              {t('document.yourDocuments')}
            </Heading>
          </Flex>

          <HStack spacing={3}>
            <Input
              w='50%'
              ml='0.5'
              pl='13'
              onChange={handleChangeSearchValue}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    getDocumentFilterByName();
                  }
                }
              }}
              placeholder={t('document.searchPlaceholder')}
              value={searchValue}
            />
            <Button
              h='39'
              borderRadius='4px'
              onClick={getDocumentFilterByName}
              type='submit'
              variant='amatrium_submit'
            >
              {t('common.search')}
            </Button>
            {isSelectedFile && isAdmin && (
              <LightMode>
                <Button h={39} borderRadius={4} colorScheme='red' onClick={onOpenDeleteDialog}>
                  {t('common.delete')}
                </Button>
              </LightMode>
            )}
          </HStack>

          <Box border={mode('0.5px solid rgb(209, 213, 219)', '')}>
            <Box overflowX={'auto'}>
              <AmatriumTableDocument
                columns={tableColumns}
                data={data || []}
                isLoading={loading}
                documentName={searchValue}
                onRowSelect={onRowSelect}
              />
            </Box>
            <Box bg='transparent'>
              <Paginate
                total={total_item}
                current={currentPage}
                pageSize={DEFAULT_SIZE_PAGE}
                onChange={(page: number) => {
                  setCurrentPage(page);
                  dispatch(
                    documentAction.fetchFiles({
                      page,
                      fileName: searchValue,
                      sortField: getStorage(STORAGE_KEY.SORT_FIELD) || 'created_at',
                      isAscending: getStorage(STORAGE_KEY.IS_ASCENDING) || false,
                    })
                  );
                }}
                disabled={loading}
              />
            </Box>
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

export default Document;
